import type { PropsWithChildren } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { bootstrap, logout } from '@/features/auth/authThunks';
import type { RootState } from '@/app/store';
import SplashScreen from '@/components/common/SplashScreen';
import { useMinSplash } from '@/hooks/useMinSplash';
import { BILLING_REGULARIZATION_PATH } from '@/lib/billingRegularization';

export default function PublicOnly({ children }: Readonly<PropsWithChildren>) {
  const dispatch = useAppDispatch();
  const { user, bootstrapped, loading } = useAppSelector((s: RootState) => s.auth);
  const workspacesList = useAppSelector((s: RootState) => s.workspace.workspacesList);
  const location = useLocation();
  const isNewPasswordPage = location.pathname === '/new-password';
  const logoutTriggered = useRef(false);
  const { t, ready } = useTranslation('common');

  useEffect(() => {
    if (!bootstrapped && !loading) {
      dispatch(bootstrap());
    }
  }, [bootstrapped, loading, dispatch]);

  // Logout user if they arrive on new-password page while authenticated
  useEffect(() => {
    if (isNewPasswordPage && user && bootstrapped && !loading && !logoutTriggered.current) {
      logoutTriggered.current = true;
      dispatch(logout());
    }
    // Reset ref when leaving the new-password page
    if (!isNewPasswordPage) {
      logoutTriggered.current = false;
    }
  }, [isNewPasswordPage, user, bootstrapped, loading, dispatch]);

  const pending =
    !bootstrapped || loading || (isNewPasswordPage && !!user && logoutTriggered.current);
  const showSplash = useMinSplash(pending, 600);

  if (showSplash) {
    return <SplashScreen label={ready ? t('initializing') : undefined} />;
  }

  if (user && !isNewPasswordPage) {
    const origin = (
      location.state as { from?: { pathname?: string; search?: string; hash?: string } } | null
    )?.from;
    const fromPath = origin?.pathname ?? '/';
    // La destination garde ses paramètres : un lien qui en porterait les perdrait à la connexion.
    const from = `${fromPath}${origin?.search ?? ''}${origin?.hash ?? ''}`;
    // Le lien de régularisation désigne lui-même son workspace : pas de choix à faire avant.
    const target =
      workspacesList && workspacesList.length > 1 && fromPath !== BILLING_REGULARIZATION_PATH
        ? '/select-workspaces'
        : from;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
